import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Eye, EyeOff, Trash2, ExternalLink, Save } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface EmployeeExternalCredentialsProps {
  employeeId: string;
  readOnly?: boolean;
}

interface ExternalCredential {
  id: string;
  access_type: string;
  username: string | null;
  password_encrypted: string | null;
  notes: string | null;
}

const PREDEFINED_ACCESS_TYPES = [
  "LWL Shop",
  "24 Efg online",
  "Mein Handwerker",
  "Strato Mail",
  "Sonstiges"
];

export function EmployeeExternalCredentials({ employeeId, readOnly = false }: EmployeeExternalCredentialsProps) {
  const { user, userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState<ExternalCredential[]>([]);
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  
  // New credential form
  const [newAccessType, setNewAccessType] = useState("");
  const [customAccessType, setCustomAccessType] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  const canManage = !readOnly && (userRole === "admin" || userRole === "hr_manager");

  useEffect(() => {
    if (user && employeeId) {
      loadCredentials();
    }
  }, [user, employeeId]);

  const loadCredentials = async () => {
    try {
      const { data, error } = await supabase
        .from("employee_external_credentials")
        .select("id, access_type, username, password_encrypted, notes")
        .eq("employee_id", employeeId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setCredentials(data || []);
    } catch (error) {
      console.error("Error loading external credentials:", error);
      toast.error("Fehler beim Laden der externen Zugänge");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (id: string) => {
    setVisiblePasswords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleAddCredential = async () => {
    const accessType = newAccessType === "Sonstiges" ? customAccessType : newAccessType;
    
    if (!accessType) {
      toast.error("Bitte wählen Sie eine Zugangsart");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("employee_external_credentials")
        .insert({
          employee_id: employeeId,
          access_type: accessType,
          username: newUsername || null,
          password_encrypted: newPassword || null,
          notes: newNotes || null,
          created_by: user?.id
        });

      if (error) throw error;

      toast.success("Externer Zugang hinzugefügt");
      setShowAddForm(false);
      resetForm();
      loadCredentials();
    } catch (error: any) {
      console.error("Error adding credential:", error);
      toast.error(error.message || "Fehler beim Hinzufügen");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredential = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from("employee_external_credentials")
        .delete()
        .eq("id", deleteId);

      if (error) throw error;

      toast.success("Externer Zugang gelöscht");
      setDeleteId(null);
      loadCredentials();
    } catch (error: any) {
      console.error("Error deleting credential:", error);
      toast.error(error.message || "Fehler beim Löschen");
    }
  };

  const resetForm = () => {
    setNewAccessType("");
    setCustomAccessType("");
    setNewUsername("");
    setNewPassword("");
    setNewNotes("");
    setShowNewPassword(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ExternalLink className="h-5 w-5" />
          Externe Zugänge & Software
        </CardTitle>
        <CardDescription>
          Zugangsdaten für externe Systeme und Software-Lösungen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing credentials list */}
        {credentials.length > 0 && (
          <div className="space-y-3">
            {credentials.map((cred) => (
              <div
                key={cred.id}
                className="p-4 bg-muted/50 rounded-lg border space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{cred.access_type}</span>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(cred.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {cred.username && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Benutzername: </span>
                    <span>{cred.username}</span>
                  </div>
                )}
                
                {cred.password_encrypted && (
                  <div className="text-sm flex items-center gap-2">
                    <span className="text-muted-foreground">Passwort: </span>
                    <span className="font-mono">
                      {visiblePasswords.has(cred.id)
                        ? cred.password_encrypted
                        : "••••••••"}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => togglePasswordVisibility(cred.id)}
                      className="h-6 w-6 p-0"
                    >
                      {visiblePasswords.has(cred.id) ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
                
                {cred.notes && (
                  <div className="text-sm text-muted-foreground">
                    {cred.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {credentials.length === 0 && !showAddForm && (
          <div className="text-center py-6 text-muted-foreground">
            Keine externen Zugänge hinterlegt
          </div>
        )}

        {/* Add form */}
        {canManage && showAddForm && (
          <div className="p-4 border rounded-lg space-y-4 bg-background">
            <div className="space-y-2">
              <Label>Zugangsart *</Label>
              <Select value={newAccessType} onValueChange={setNewAccessType}>
                <SelectTrigger>
                  <SelectValue placeholder="Zugangsart wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {PREDEFINED_ACCESS_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {newAccessType === "Sonstiges" && (
                <Input
                  placeholder="Eigene Bezeichnung eingeben..."
                  value={customAccessType}
                  onChange={(e) => setCustomAccessType(e.target.value)}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>Benutzername</Label>
              <Input
                placeholder="Benutzername eingeben..."
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Passwort</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Passwort eingeben..."
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-0 top-0 h-full px-3"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notizen</Label>
              <Input
                placeholder="Optionale Notizen..."
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleAddCredential} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Speichern
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
              >
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {/* Add button */}
        {canManage && !showAddForm && (
          <Button
            variant="outline"
            onClick={() => setShowAddForm(true)}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            Externen Zugang hinzufügen
          </Button>
        )}

        {/* Delete confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Zugang löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie diesen externen Zugang wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteCredential}>
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}